import {OktaAuth} from '@okta/okta-auth-js'
import {CLIENT_ID, ISSUER, REDIRECT_URI, ENV} from './envConfig'

// Determine if we're in development mode
// @ts-ignore - String comparison is intentional, will be replaced at runtime
const isDevelopment = ENV === 'development' || import.meta.env.MODE === 'development'
// Check if we're on localhost
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

// @ts-ignore - We're using a more complete config than the type definition allows
export const oktaConfig = {
  clientId: CLIENT_ID,
  issuer: ISSUER,
  redirectUri: REDIRECT_URI,
  responseType: 'code', // Explicitly set response type to 'code' for authorization code flow
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true, // Always use PKCE for security
  tokenManager: {
    expireEarlySeconds: 2 * 60, // Match B2B Portal: renew token before 2 mins of expiration
  },
  cookies: {
    secure: !(isDevelopment && isLocalhost), // Only secure in production or non-localhost
    sameSite: 'lax', // Restrict cookies to same-site
  },
  testing: {
    disableHttpsCheck: isDevelopment, // Disable HTTPS check in development
  },
}

// Initialize and export Okta Auth instance with better error handling
const createOktaAuth = (): OktaAuth => {
  try {
    // @ts-ignore - We're using a more complete config than the type definition allows
    const auth = new OktaAuth(oktaConfig)
    return auth
  } catch (error) {
    console.error('Failed to initialize Okta Auth SDK:', error)
    // Fallback with minimal requirements and secure cookies disabled for development
    return new OktaAuth({
      clientId: CLIENT_ID,
      issuer: ISSUER,
      redirectUri: REDIRECT_URI,
      cookies: {secure: false},
    })
  }
}

// Create and export the Okta Auth instance
export const oktaAuth = createOktaAuth()

/**
 * Retrieves the access token from Okta token storage
 * @returns The access token or null if not found
 */
export const oktaAccessToken = () => {
  try {
    // First try to get token from Okta SDK
    if (oktaAuth?.authStateManager) {
      const authState = oktaAuth.authStateManager.getAuthState()
      if (authState?.isAuthenticated && authState?.accessToken?.accessToken) {
        return authState.accessToken.accessToken
      }
    }

    // Fallback to localStorage
    const tokenStorage = localStorage.getItem('okta-token-storage')
    if (!tokenStorage) {
      console.debug('[oktaAccessToken] No token storage found in localStorage')
      return null
    }

    const authStateStorage = JSON.parse(tokenStorage)
    const accessToken = authStateStorage?.accessToken?.accessToken

    if (!accessToken) {
      console.debug('[oktaAccessToken] No access token found in token storage', Object.keys(authStateStorage ?? {}))
      return null
    }

    // For development debugging, check token expiration
    if (isDevelopment && authStateStorage?.accessToken?.expiresAt) {
      const expiresAt = new Date(authStateStorage.accessToken.expiresAt * 1000)
      const now = new Date()
      const timeLeft = Math.round((expiresAt.getTime() - now.getTime()) / 1000 / 60)

      if (timeLeft < 0) {
        console.warn(`[oktaAccessToken] Token expired ${Math.abs(timeLeft)} minutes ago`)
      } else {
        console.debug(`[oktaAccessToken] Token valid for ${timeLeft} more minutes`)
      }
    }

    return accessToken
  } catch (error) {
    console.error('[oktaAccessToken] Error retrieving access token:', error)
    return null
  }
}
